import { Set } from 'immutable'
import { conversions as C } from 'lib/units'
import smartDate from '@qrohlf/smart-date'
import moment from 'moment'
import _ from 'lodash'
import seedrandom from 'seedrandom'
import uuidv4 from 'uuid/v4'
import queryString from 'query-string'
import { defineMessages } from 'react-intl'

export const DASH_LAUNCH = '2019-03-11'
export const ITUNES_APP_URL = 'https://itunes.apple.com/us/app/ride-with-gps-bike-computer/id893687399?mt=8'
export const GPLAY_APP_URL = 'https://play.google.com/store/apps/details?id=com.ridewithgps.mobile&hl=en_US'

export const immutableSumBy = (collection, fn) => (
  collection.reduce((a, b) => a + fn(b), 0)
)

export const camelCaseKeys = obj => _.mapKeys(obj, (v, k) => _.camelCase(k))

// Promise.all, but for Objects.
// {key1: Promise, key2: Promise} => {key1: result, key2: result}
export const promiseMapAll = obj => {
  const kv = Object.entries(obj)
  // I'm being conservative here, the ordering of Object.keys and Object.values
  // is not guaranteed to match in all browsers
  const keys = kv.map(([k, v]) => k)
  const values = kv.map(([k, v]) => v)
  return Promise.all(values)
    .then(
      results => results.reduce((obj, r, index) => ({ ...obj, [keys[index]]: r }), {})
    )
}

// per Cullen's advice, 2 photos per user is probably the maximum that we should
// chuck at the server at once. This is implemented as a function so that we
// have the ability do some things in the future like scaling the concurrency
// based on the  user's connection or device type
export const getPhotoUploadMaxConcurrency = () => 2

export const uuid = uuidv4

export const buildQueryString = (params) => _.map(params, (v, k) => [k, v].map(encodeURIComponent).join('=')).join('&')

export const queryStringSet = (location, key, value, history) => {
  history.replace(location.pathname + '?' +
    queryString.stringify({
      ...location.query,
      [key]: value
    })
  )
}

export const currentURLWithQueryParams = (params) => (
  window.location.origin +
  window.location.pathname + '?' +
  queryString.stringify({ ...queryString.parse(window.location.search), ...params })
)

// using queryString here to ensure correct combination of ? and &
export const urlWithPrivacyCode = (url, privacyCode) => {
  if (!privacyCode) { return url }
  const parsed = queryString.parseUrl(url)
  return parsed.url + '?' + queryString.stringify({ ...parsed.query, privacy_code: privacyCode })
}

const contestStart = moment('2017-03-01T00:00:00-08:00')
export const contestEnd = moment('2017-05-09T23:59:00-08:00')

export const isDuringContest = (ride) => moment(ride.departed_at).isBetween(contestStart, contestEnd)
export const isDateDuringContest = (date) => moment(date).isBetween(contestStart, contestEnd)

export const VOTING_ACTIVE = false

export const getCurrentUserRaw = () => (window.rwgps && window.rwgps.config && window.rwgps.config.currentUser) || {}

export const currentUserHasPrivilege = (priv) => {
  const privileges = (window.rwgps && window.rwgps.config && window.rwgps.config.privileges) || {}
  return privileges[priv]
}

export const getCurrentUser = () => {
  const {
    id,
    name,
    first_name: firstName,
    last_name: lastName,
    email,
    lat,
    lng,
    preferences,
    account_level: accountLevel,
    highlighted_photo_id: highlightedPhotoId,
    num_unread_messages: numUnreadMessages,
    notifications_serialized: notifications,
    time_zone: timeZone,
    weeks_start_on: weekStartsOn,
    eligible_for_onetime_trial: eligibleForOnetimeTrial,
    has_subscription: hasSubscription
  } = getCurrentUserRaw()

  const numUnreadNotifications = (notifications && notifications.filter((n) => n.read_status === 'unseen').length) || 0

  return {
    id,
    name,
    firstName,
    lastName,
    email,
    lat,
    lng,
    preferences: preferences || {},
    accountLevel,
    highlightedPhotoId,
    notifications,
    timeZone,
    numUnreadMessages,
    numUnreadNotifications,
    weekStartsOn,
    eligibleForOnetimeTrial,
    hasSubscription
  }
}

// https://github.com/ridewithgps/ridewithgps/blob/9d6e721dd3ccf2e3e8b38dd401b3549c3263487b/app/controllers/application_controller.rb#L950
export const getPreferences = () => (window.rwgps?.config?.baseCurrentUser || window.rwgps?.config?.currentUser || {})?.preferences || {}

export const userIsAdmin = !!(window.rwgps && window.rwgps.config && window.rwgps.config.currentUser && window.rwgps.config.currentUser.account_level === 11)

export const getSurrogateLogin = () => (window.rwgps && window.rwgps.config && window.rwgps.config.surrogateLogin)

export const getBaseCurrentUser = () => (window.rwgps && window.rwgps.config && window.rwgps.config.baseCurrentUser)

// deprecated really, don't use this thing
export const intThatWraps = (value, max) => ({
  value,
  next: () => intThatWraps((value + 1) % max, max),
  prev: () => intThatWraps(value > 0 ? value - 1 : max - 1, max)
})

export const stripNewlines = (string, replace = '') => string.replace(/(\r\n|\n|\r)/gm, replace)

export const clampString = (string, length, endCap = '...') => (
  (string ?? '').length <= length
    ? string
    : string.slice(0, length).trim() + endCap
)

export const clampStringWords = (string, length, endCap = '...') => {
  const s = clampString(string, length, '')
  if (s === string) { return string }
  return s.substring(0, s.lastIndexOf(' ')) + endCap
}

export const capitalize = string => string ? string[0].toUpperCase() + string.slice(1) : string

// seeded Fisher-Yates shuffle algorithm adapted from https://bost.ocks.org/mike/shuffle/
export const seededShuffle = (original, seed) => {
  const array = original.slice()
  let m = array.length
  let t
  let i
  const rng = seedrandom(seed)

  // While there remain elements to shuffle…
  while (m) {
    // Pick a remaining element…
    i = Math.floor(rng() * m--)

    // And swap it with the current element.
    t = array[m]
    array[m] = array[i]
    array[i] = t
  }

  return array
}

// O(log32 N) efficient filter by key for collections
export const keyIn = (keys) => {
  var keySet = Set(keys)
  return (v, k) => {
    return keySet.has(k)
  }
}

// wrap ints in the range [0, max)
export const wrapInt = (value, max) => {
  return ((value % max) + max) % max
}

// quick and dirty date compare
export const compareDates = (a, b) => {
  const dateA = new Date(a)
  const dateB = new Date(b)
  return dateB.getTime() - dateA.getTime()
}

export const clamp = (min, max, num) => Math.min(Math.max(num, min), max)

export const shallowCompareSubset = (current, next, keys) => keys.map(k => current[k] === next[k]).some(v => !v)

// DANGER WILL ROBINSON - this function assumes that you are passing react
// props or state objects that will have the same shape between iterations.
// This allows a significant performance improvment based on only having to
// iterate over the keys of a single object, without allocations or deduping.
// If you try to pass it two objects that do NOT have the same set of keys,
// you're gonna have a bad time.
export const shallowCompareObjects = (current, next) => shallowCompareSubset(current, next, Object.keys(current))

const parts = defineMessages({
  rideVerb: {
    id: 'goalDetailsParts.rideVerb',
    defaultMessage: 'Ride',
    description: 'ride (verb)'
  },
  competeToSee: {
    id: 'goalDetailsParts.competeToSee',
    defaultMessage: 'Compete to see who can ride'
  },
  dateSpan: {
    id: 'goalDetailsParts.dateSpan',
    defaultMessage: 'between {startDate} and {endDate}'
  },
  mostDistance: {
    id: 'goalDetailsParts.mostDistance',
    defaultMessage: 'the most distance'
  },
  mostElevation: {
    id: 'goalDetailsParts.mostElevation',
    defaultMessage: 'the most elevation gain'
  },
  mostTime: {
    id: 'goalDetailsParts.mostTime',
    defaultMessage: 'the most time'
  }
})

// NEEDS TESTS
export const goalDetails = (goal, metricUnits = false, intl) => {
  const fmt = intl.formatMessage
  const max = goal.getIn(['goal_params', 'max'])
  const prefix = (max ? fmt(parts.rideVerb) : fmt(parts.competeToSee)) + ' '
  const suffix = ' ' + fmt(parts.dateSpan, {
    startDate: smartDate(goal.get('starts_on')),
    endDate: goal.get('ends_on') && smartDate(goal.get('ends_on')) // I guess it's somehow possible to generate goals without end dates??
  })
  let units, challengeSuffix
  switch (goal.get('goal_type')) {
    case 'distance': {
      challengeSuffix = fmt(parts.mostDistance)
      if (max) {
        units = metricUnits
          ? (C.metersToKm(max).toLocaleString('en-US', { useGrouping: true, maximumFractionDigits: 0 }) + ' km')
          : (C.metersToMiles(max).toLocaleString('en-US', { useGrouping: true, maximumFractionDigits: 0 }) + ' miles')
      }
      break
    }
    case 'elevation_gain': {
      challengeSuffix = fmt(parts.mostElevation)
      if (max) {
        units = metricUnits
          ? (max.toLocaleString('en-US', { useGrouping: true, maximumFractionDigits: 0 }) + ' meters of elevation gain')
          : (C.metersToFeet(max).toLocaleString('en-US', { useGrouping: true, maximumFractionDigits: 0 }) + ' feet of elevation gain')
      }
      break
    }
    case 'moving_time': {
      challengeSuffix = fmt(parts.mostTime)
      if (max) {
        units = (max / 60 / 60).toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 0 }) + ' hours'
      }
      break
    }
    default: {
      challengeSuffix = ''
      units = ''
    }
  }
  return prefix + (max ? units : challengeSuffix) + suffix
}

export const applyDefaults = (defaults, options = {}) => {
  const filteredOptions = _.pickBy(options, (v) => v !== undefined)
  return { ...defaults, ...filteredOptions }
}

export const experimentEnabled = exp => _.get(window, 'rwgps.config.labs.' + exp, false)

// webGL detection snippet from
// https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
export const checkWebGLSupport = () => {
  // Create canvas element. The canvas is not added to the
  // document itself, so it is never displayed in the
  // browser window.
  var canvas = document.createElement('canvas')
  // Get WebGLRenderingContext from canvas element.
  var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
  // Report the result.
  if (gl && gl instanceof window.WebGLRenderingContext) {
    return true
  } else {
    return false
  }
}

// https://github.com/ridewithgps/ridewithgps/issues/9080
// https://stackoverflow.com/a/326076
export const renderingInIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export const downloadFile = filePath => {
  let link = document.createElement('a')
  link.href = filePath
  link.download = filePath.substr(filePath.lastIndexOf('/') + 1)
  link.click()
}
